import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { formatMoney } from '../../utils/format';
import { LINEUP_FORMATS, lineupTotals, fmtCap } from './poolFx';

// Pool settings during the pool. Everyone sees them; only the host can change them.
// Changeable now: lineup mode on/off (takes effect from the next game, points already earned stay).
export default function PoolSettings({ pool, userId, lang = 'en', onChanged }) {
  const fr = lang === 'fr';
  const t = fr ? TR.fr : TR.en;
  const isHost = pool.creator_id === userId;
  const [picks, setPicks] = useState([]);
  const [changes, setChanges] = useState([]);
  const [names, setNames] = useState({});
  const [lineupOn, setLineupOn] = useState(!!pool.lineup_mode);
  const [format, setFormat] = useState(pool.lineup_format || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [{ data: pk }, { data: ch }, { data: mem }] = await Promise.all([
      supabase.from('pool_picks').select('user_id, position').eq('pool_id', pool.id),
      supabase.from('pool_setting_changes').select('*').eq('pool_id', pool.id).order('created_at', { ascending: false }),
      supabase.from('pool_members').select('user_id, profiles(username)').eq('pool_id', pool.id),
    ]);
    setPicks(pk || []);
    setChanges(ch || []);
    setNames(Object.fromEntries((mem || []).map((m) => [m.user_id, m.profiles?.username || '—'])));
  }, [pool.id]);
  useEffect(() => { load(); }, [load]);

  // the saved values changed (realtime / after saving): start from them again
  useEffect(() => { setLineupOn(!!pool.lineup_mode); setFormat(pool.lineup_format || null); }, [pool.lineup_mode, pool.lineup_format]);

  // most players any team drafted at each position — a lineup size must have room for them
  const most = useMemo(() => {
    const per = {};
    picks.forEach((p) => { const k = `${p.user_id}|${p.position}`; per[k] = (per[k] || 0) + 1; });
    const m = { F: 0, D: 0, G: 0 };
    Object.entries(per).forEach(([k, n]) => { const pos = k.split('|')[1]; if (m[pos] != null) m[pos] = Math.max(m[pos], n); });
    return m;
  }, [picks]);
  const fits = (key) => { const tot = lineupTotals(key); return tot.F >= most.F && tot.D >= most.D && tot.G >= most.G; };
  // best default: the smallest size that fits everyone
  const suggested = Object.keys(LINEUP_FORMATS).find(fits) || 'full';

  const dirty = lineupOn !== !!pool.lineup_mode || (lineupOn && format !== pool.lineup_format);
  const canSave = dirty && (!lineupOn || (format && fits(format)));

  const toggle = (on) => {
    setError('');
    setLineupOn(on);
    if (on && (!format || !fits(format))) setFormat(suggested);
  };
  const reset = () => { setLineupOn(!!pool.lineup_mode); setFormat(pool.lineup_format || null); setError(''); };

  const save = async () => {
    if (!canSave || busy) return;
    setBusy(true); setError('');
    const { data, error: err } = await supabase.rpc('set_pool_lineup_mode', {
      p_user_id: userId, p_pool_id: pool.id, p_enabled: lineupOn, p_format: lineupOn ? format : null,
    });
    setBusy(false);
    if (err || !data?.success) { setError(err?.message || data?.error || t.errSave); return; }
    await load();
    onChanged?.();
  };

  const capText = pool.cap_mode && pool.cap_amount != null
    ? `${fmtCap(pool.cap_amount)} · ${pool.cap_mode === 'hard' ? t.hardCap : t.penalties}`
    : t.off;
  const valueOfChange = (v) => (v === 'off' ? t.off : t.formats[v] || v);

  return (
    <div className="ps">
      <div className="ps-head">
        <h3 className="ps-title">{t.title}</h3>
        <span className="ps-sub">{isHost ? t.hostSub : t.memberSub}</span>
      </div>

      {/* ── Lineup mode (changeable) ── */}
      <section className="ps-card">
        <div className="pool-form-row">
          <label className="pool-toggle-row">
            <span>
              <b>{t.lineupMode}</b>
              <small>{t.lineupHint}</small>
            </span>
            <input type="checkbox" role="switch" className="menu-switch" checked={lineupOn}
              disabled={!isHost || busy} onChange={(e) => toggle(e.target.checked)} />
          </label>
        </div>

        {lineupOn && (
          <div className="pool-form-row">
            <label>{t.lineupSize}</label>
            <div className="pool-formats ps-formats">
              {Object.keys(LINEUP_FORMATS).map((key) => {
                const lines = LINEUP_FORMATS[key];
                const tot = lineupTotals(key);
                const ok = fits(key);
                return (
                  <button key={key} type="button" disabled={!isHost || !ok || busy}
                    className={`pool-format ${format === key ? 'active' : ''} ${ok ? '' : 'too-small'}`}
                    onClick={() => setFormat(key)} title={ok ? '' : t.tooSmall}>
                    <span className="pool-format-name">{t.formats[key]}</span>
                    <span className="pool-format-lines">
                      {['F', 'D', 'G'].map((pos) => {
                        const main = lines.filter((x) => x.pos === pos && x.mult > 0);
                        const ext = lines.filter((x) => x.pos === pos && x.mult === 0).reduce((n, x) => n + x.cap, 0);
                        return (
                          <span key={pos}>
                            <b>{pos}</b> {pos === 'G' ? main.length : `${main.length} ${pos === 'F' ? t.lineWord : t.pairWord}`}{ext ? ` + ${ext}` : ''}
                          </span>
                        );
                      })}
                    </span>
                    <span className="pool-format-total">{ok ? `${tot.F + tot.D + tot.G} ${t.playersWord}` : t.tooSmall}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isHost && dirty && (
          <div className="ps-save">
            <span className="ps-save-note">{lineupOn ? t.onNote : t.offNote}</span>
            <span className="ps-save-actions">
              <button type="button" className="pool-btn-cancel" onClick={reset} disabled={busy}>{t.cancel}</button>
              <button type="button" className="pool-btn-confirm" onClick={save} disabled={!canSave || busy}>{busy ? t.saving : t.save}</button>
            </span>
          </div>
        )}
        {error && <div className="pool-form-error">{error}</div>}
      </section>

      {/* ── Everything else (set when the pool was created) ── */}
      <section className="ps-card">
        <h4 className="ps-card-title">{t.fixedTitle}</h4>
        <div className="ps-rows">
          <div className="ps-row"><span>{t.format}</span><b>{pool.format === 'playoff' ? t.playoffs : t.regular}</b></div>
          <div className="ps-row"><span>{t.entryFee}</span><b>{formatMoney(pool.entry_fee, lang)}</b></div>
          <div className="ps-row">
            <span>{t.roster}</span>
            <b>{pool.slots_f != null ? `${pool.slots_f}F · ${pool.slots_d ?? 0}D · ${pool.slots_g ?? 0}G · ${pool.slots_t ?? 1}T` : `${pool.roster_size} ${t.playersWord}`}</b>
          </div>
          <div className="ps-row"><span>{t.freeAgency}</span><b>{pool.free_agency ? t.faOn(pool.fa_pickups_per_month ?? 2) : t.off}</b></div>
          <div className="ps-row"><span>{t.salaryCap}</span><b>{capText}</b></div>
        </div>
      </section>

      {/* ── History ── */}
      <section className="ps-card">
        <h4 className="ps-card-title">{t.history}</h4>
        {changes.length === 0 && <div className="ps-empty">{t.noChanges}</div>}
        {changes.map((c) => (
          <div key={c.id} className="ps-change">
            <span className="ps-change-when">{new Date(c.created_at).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' })}</span>
            <span className="ps-change-what">
              <b>{names[c.user_id] || '—'}</b> · {t.lineupMode}: {valueOfChange(c.old_value)} → <b>{valueOfChange(c.new_value)}</b>
            </span>
          </div>
        ))}
      </section>
    </div>
  );
}

const TR = {
  en: {
    title: 'Pool settings',
    hostSub: 'You’re the host — changes apply from the next game. Points already earned never change.',
    memberSub: 'Only the host can change these. Every change shows up in the history below.',
    lineupMode: 'Lineup mode',
    lineupHint: 'Lines + extras: only your lines score, Line 1 and Pair 1 count double. Off: every player scores ×1.',
    lineupSize: 'Lineup size',
    formats: { small: 'Small', medium: 'Medium', large: 'Large', full: 'Full' },
    lineWord: 'lines', pairWord: 'pairs', playersWord: 'players',
    tooSmall: 'Too small for the drafted teams',
    onNote: 'Everyone gets default lines (draft order fills Line 1 first) and can change them right away.',
    offNote: 'From the next game every player scores ×1. Turning it back on gives everyone default lines again.',
    cancel: 'Cancel', save: 'Save', saving: 'Saving…',
    errSave: 'Could not save the settings',
    fixedTitle: 'Set at creation',
    format: 'Format', regular: 'Regular season', playoffs: 'Playoffs',
    entryFee: 'Entry fee', roster: 'Roster',
    freeAgency: 'Free agency', faOn: (n) => `On · ${n} pickups a month`,
    salaryCap: 'Salary cap', hardCap: 'hard cap', penalties: 'penalties',
    off: 'Off',
    history: 'History', noChanges: 'No changes yet',
  },
  fr: {
    title: 'Réglages du pool',
    hostSub: 'Tu es l’hôte — les changements s’appliquent dès le prochain match. Les points déjà gagnés ne changent jamais.',
    memberSub: 'Seul l’hôte peut les changer. Chaque changement apparaît dans l’historique plus bas.',
    lineupMode: 'Mode alignement',
    lineupHint: 'Trios + réserves : seuls tes trios marquent, le 1er trio et la 1re paire comptent double. Désactivé : chaque joueur marque ×1.',
    lineupSize: 'Taille de l’alignement',
    formats: { small: 'Petit', medium: 'Moyen', large: 'Grand', full: 'Complet' },
    lineWord: 'trios', pairWord: 'paires', playersWord: 'joueurs',
    tooSmall: 'Trop petit pour les équipes repêchées',
    onNote: 'Chacun reçoit un alignement par défaut (l’ordre du repêchage remplit le 1er trio) et peut le changer tout de suite.',
    offNote: 'Dès le prochain match, chaque joueur marque ×1. Le réactiver redonne un alignement par défaut à tout le monde.',
    cancel: 'Annuler', save: 'Enregistrer', saving: 'Enregistrement…',
    errSave: 'Impossible d’enregistrer les réglages',
    fixedTitle: 'Choisi à la création',
    format: 'Format', regular: 'Saison régulière', playoffs: 'Séries',
    entryFee: 'Inscription', roster: 'Alignement',
    freeAgency: 'Joueurs autonomes', faOn: (n) => `Activé · ${n} signatures par mois`,
    salaryCap: 'Plafond salarial', hardCap: 'plafond strict', penalties: 'pénalités',
    off: 'Désactivé',
    history: 'Historique', noChanges: 'Aucun changement pour l’instant',
  },
};
